/**
 * MySQL/MariaDB-specific database queries
 *
 * Uses information_schema for all metadata. MySQL has no schema concept
 * like MSSQL/PostgreSQL: database = schema. The schema_name column in
 * results is set to the database name.
 *
 * System databases filtered: information_schema, performance_schema, mysql, sys
 */

const SYSTEM_DBS = "'information_schema', 'performance_schema', 'mysql', 'sys'";

export const mysqlQueries = {
  /** Get all databases (excluding system databases) */
  databases: `
    SELECT SCHEMA_NAME AS name
    FROM information_schema.SCHEMATA
    WHERE SCHEMA_NAME NOT IN (${SYSTEM_DBS})
    ORDER BY SCHEMA_NAME;
  `,

  /** MySQL has no schema concept (database = schema): the one "schema" is the current database. */
  schemas: `SELECT DATABASE() AS name;`,

  /** Get tables for the current database */
  tables: `
    SELECT
      CONCAT(TABLE_SCHEMA, '.', TABLE_NAME) AS id,
      TABLE_NAME AS name,
      TABLE_SCHEMA AS schema_name,
      'table' AS object_type
    FROM information_schema.TABLES
    WHERE TABLE_SCHEMA = DATABASE()
      AND TABLE_TYPE = 'BASE TABLE'
    ORDER BY TABLE_NAME;
  `,

  /** Get views for the current database */
  views: `
    SELECT
      CONCAT(TABLE_SCHEMA, '.', TABLE_NAME) AS id,
      TABLE_NAME AS name,
      TABLE_SCHEMA AS schema_name,
      'view' AS object_type
    FROM information_schema.VIEWS
    WHERE TABLE_SCHEMA = DATABASE()
    ORDER BY TABLE_NAME;
  `,

  /** Get stored procedures for the current database */
  storedProcedures: `
    SELECT
      CONCAT(ROUTINE_SCHEMA, '.', ROUTINE_NAME) AS id,
      ROUTINE_NAME AS name,
      ROUTINE_SCHEMA AS schema_name,
      'stored-procedure' AS object_type
    FROM information_schema.ROUTINES
    WHERE ROUTINE_SCHEMA = DATABASE()
      AND ROUTINE_TYPE = 'PROCEDURE'
    ORDER BY ROUTINE_NAME;
  `,

  /** Get functions for the current database */
  functions: `
    SELECT
      CONCAT(ROUTINE_SCHEMA, '.', ROUTINE_NAME) AS id,
      ROUTINE_NAME AS name,
      ROUTINE_SCHEMA AS schema_name,
      'function' AS object_type
    FROM information_schema.ROUTINES
    WHERE ROUTINE_SCHEMA = DATABASE()
      AND ROUTINE_TYPE = 'FUNCTION'
    ORDER BY ROUTINE_NAME;
  `,

  /**
   * Get columns for a table
   * @param schema - Database name (MySQL: database = schema)
   * @param tableName - Table name (will be escaped)
   */
  columns: (schema: string, tableName: string) => `
    SELECT
      c.COLUMN_NAME AS column_name,
      c.COLUMN_TYPE AS data_type,
      c.CHARACTER_MAXIMUM_LENGTH AS max_length,
      c.NUMERIC_PRECISION AS \`precision\`,
      c.NUMERIC_SCALE AS scale,
      CASE WHEN c.IS_NULLABLE = 'YES' THEN TRUE ELSE FALSE END AS is_nullable,
      CASE WHEN c.COLUMN_KEY = 'PRI' THEN TRUE ELSE FALSE END AS is_primary_key,
      CASE WHEN c.EXTRA LIKE '%auto_increment%' THEN TRUE ELSE FALSE END AS is_identity,
      c.COLUMN_DEFAULT AS default_value,
      c.ORDINAL_POSITION AS ordinal_position
    FROM information_schema.COLUMNS c
    WHERE c.TABLE_SCHEMA = '${schema.replace(/'/g, "''")}'
      AND c.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
    ORDER BY c.ORDINAL_POSITION;
  `,

  /**
   * Get table constraints (PK, FK, UNIQUE, CHECK)
   * CHECK constraints available in MySQL 8.0.16+
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  tableConstraints: (schema: string, tableName: string) => `
    SELECT
      tc.CONSTRAINT_NAME AS constraint_name,
      tc.CONSTRAINT_TYPE AS constraint_type,
      kcu.COLUMN_NAME AS column_name,
      kcu.REFERENCED_TABLE_SCHEMA AS referenced_schema,
      kcu.REFERENCED_TABLE_NAME AS referenced_table,
      kcu.REFERENCED_COLUMN_NAME AS referenced_column,
      cc.CHECK_CLAUSE AS check_definition,
      kcu.ORDINAL_POSITION AS column_ordinal
    FROM information_schema.TABLE_CONSTRAINTS tc
    LEFT JOIN information_schema.KEY_COLUMN_USAGE kcu
      ON tc.CONSTRAINT_NAME = kcu.CONSTRAINT_NAME
      AND tc.TABLE_SCHEMA = kcu.TABLE_SCHEMA
      AND tc.TABLE_NAME = kcu.TABLE_NAME
    LEFT JOIN information_schema.CHECK_CONSTRAINTS cc
      ON tc.CONSTRAINT_NAME = cc.CONSTRAINT_NAME
      AND tc.CONSTRAINT_SCHEMA = cc.CONSTRAINT_SCHEMA
    WHERE tc.TABLE_SCHEMA = '${schema.replace(/'/g, "''")}'
      AND tc.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
      AND tc.CONSTRAINT_TYPE IN ('PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'CHECK')
    ORDER BY tc.CONSTRAINT_TYPE, tc.CONSTRAINT_NAME, kcu.ORDINAL_POSITION;
  `,

  /**
   * Get table indexes (excluding PRIMARY)
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  tableIndexes: (schema: string, tableName: string) => `
    SELECT
      INDEX_NAME AS index_name,
      COLUMN_NAME AS column_name,
      CASE WHEN NON_UNIQUE = 0 THEN TRUE ELSE FALSE END AS is_unique,
      INDEX_TYPE AS index_type,
      SEQ_IN_INDEX AS key_ordinal
    FROM information_schema.STATISTICS
    WHERE TABLE_SCHEMA = '${schema.replace(/'/g, "''")}'
      AND TABLE_NAME = '${tableName.replace(/'/g, "''")}'
      AND INDEX_NAME != 'PRIMARY'
    ORDER BY INDEX_NAME, SEQ_IN_INDEX;
  `,

  /**
   * Get table triggers for a table
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  tableTriggers: (schema: string, tableName: string) => `
    SELECT
      TRIGGER_NAME AS trigger_name,
      ACTION_TIMING AS trigger_timing,
      EVENT_MANIPULATION AS trigger_events,
      1 AS is_enabled
    FROM information_schema.TRIGGERS
    WHERE EVENT_OBJECT_SCHEMA = '${schema.replace(/'/g, "''")}'
      AND EVENT_OBJECT_TABLE = '${tableName.replace(/'/g, "''")}'
    ORDER BY TRIGGER_NAME;
  `,

  /**
   * Get default constraints for a table
   * MySQL doesn't have named default constraints (they are column attributes).
   * Returns empty result set.
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  tableDefaultConstraints: (schema: string, tableName: string) => {
    void schema;
    void tableName;
    return `
      SELECT
        '' AS constraint_name,
        'DEFAULT' AS constraint_type,
        '' AS column_name,
        '' AS default_definition
      WHERE 0;
    `;
  },

  /**
   * Get table statistics for a table
   * MySQL doesn't have a statistics catalog in the same sense as MSSQL/PostgreSQL.
   * Returns empty result set.
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  tableStatistics: (schema: string, tableName: string) => {
    void schema;
    void tableName;
    return `
      SELECT
        '' AS statistic_name,
        '' AS column_name,
        0 AS column_ordinal,
        0 AS auto_created,
        0 AS user_created,
        NULL AS last_updated
      WHERE 0;
    `;
  },

  /**
   * Get table-level options (storage engine, row format, collation,
   * auto_increment seed, table comment).
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  tableOptions: (schema: string, tableName: string) => `
    SELECT
      ENGINE AS storage_engine,
      ROW_FORMAT AS row_format,
      TABLE_COLLATION AS table_collation,
      AUTO_INCREMENT AS auto_increment,
      TABLE_COMMENT AS comment
    FROM information_schema.tables
    WHERE table_schema = '${schema.replace(/'/g, "''")}'
      AND table_name = '${tableName.replace(/'/g, "''")}';
  `,

  /**
   * Get per-column enrichments (generated-column expression, EXTRA flags,
   * collation, comment).
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  columnEnrichments: (schema: string, tableName: string) => `
    SELECT
      COLUMN_NAME AS column_name,
      GENERATION_EXPRESSION AS generation_expression,
      EXTRA AS extra,
      COLLATION_NAME AS collation,
      COLUMN_COMMENT AS comment
    FROM information_schema.columns
    WHERE table_schema = '${schema.replace(/'/g, "''")}'
      AND table_name = '${tableName.replace(/'/g, "''")}';
  `,

  /**
   * Get partition definition (method, expression, sub-method).
   * Returns zero rows for non-partitioned tables.
   * @param schema - Database name
   * @param tableName - Table name (will be escaped)
   */
  partition: (schema: string, tableName: string) => `
    SELECT DISTINCT
      PARTITION_METHOD AS method,
      PARTITION_EXPRESSION AS expression,
      SUBPARTITION_METHOD AS sub_method
    FROM information_schema.partitions
    WHERE table_schema = '${schema.replace(/'/g, "''")}'
      AND table_name = '${tableName.replace(/'/g, "''")}'
      AND PARTITION_NAME IS NOT NULL
    LIMIT 1;
  `,

  /**
   * Get database properties
   * @param databaseName - Database name (will be escaped)
   */
  databaseProperties: (databaseName: string) => `
    SELECT
      SCHEMA_NAME AS database_name,
      DEFAULT_COLLATION_NAME AS collation,
      '' AS owner,
      DEFAULT_CHARACTER_SET_NAME AS encoding,
      '' AS ctype
    FROM information_schema.SCHEMATA
    WHERE SCHEMA_NAME = '${databaseName.replace(/'/g, "''")}';
  `,

  /** Default database for MySQL */
  defaultDatabase: 'mysql',

  /** Fallback databases if query fails */
  fallbackDatabases: ['mysql', 'information_schema'],
};
